import { formatMoney } from "@khazzin/i18n/money";
import { todayIn } from "@khazzin/ui/patterns";
import { Button } from "@khazzin/ui/primitives/Button";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { useApi } from "../../api";
import { useI18n } from "../../i18n";
import { cashRows, inHand } from "../../modules/cod/cod";
import { useOffline } from "../../offline/Offline";
import { useEarnings } from "./Cash";
import { figuresSince, type PeriodFigures, periods, todayFigures } from "./figures";
import { timeZone } from "./Shell";
import { useStops } from "./Today";

/**
 * The Dashboard (ADJ-1022-8b), the app's landing tab: open whether or not the
 * driver is on shift or has stops today. Today's progress and the cash in
 * hand read the cached day, so they show with no signal; this week, this
 * month and My Earnings are read online, and say so when there is none.
 */
export function Dashboard() {
	const { t, locale } = useI18n();
	const { day, online, ready, wallet, outbox, tenderOptions } = useOffline();
	const { stops } = useStops();
	const api = useApi();
	const earnings = useEarnings();
	const span = periods(todayIn(timeZone));
	const kpi = useQuery({
		queryKey: ["driver", "kpi", span.from, span.to],
		queryFn: ({ signal }) =>
			api.getDeliveryKpi("drivers", { from: span.from, to: span.to, signal }),
		enabled: online,
	});
	const cod = useQuery({
		queryKey: ["driver", "codCollected", span.from, span.to],
		queryFn: ({ signal }) =>
			api.getAnalyticsMetric({
				metricKey: "cod_collected",
				from: span.from,
				to: span.to,
				groupBy: "date",
				signal,
			}),
		enabled: online,
	});

	const money = (amountMinor: number, currency: string) =>
		formatMoney({ amountMinor, currency }, locale);
	const today = todayFigures(stops);
	const held = Object.entries(
		inHand(cashRows(wallet, outbox, day?.shipments ?? {}, tenderOptions)),
	);
	const offline = t(!online ? "driver.dashboard.needSignal" : "driver.today.loading");

	return (
		<div className="flex flex-col gap-4">
			<section aria-label={t("driver.dashboard.today")} className="flex flex-col gap-3 card p-4">
				<h2 className="text-[15px] font-bold text-fg">{t("driver.dashboard.today")}</h2>
				{day === null ? (
					<p className="text-[14px] text-fg-muted">
						{t(ready && !online ? "driver.today.needSignal" : "driver.today.loading")}
					</p>
				) : today.total === 0 ? (
					<p className="text-[14px] text-fg-muted">{t("driver.dashboard.noStops")}</p>
				) : (
					<>
						<p className="text-[13px] text-fg-muted">
							{t("driver.today.progress", { done: today.done, total: today.total })}
						</p>
						<div
							role="progressbar"
							aria-label={t("driver.today.progressLabel")}
							aria-valuemin={0}
							aria-valuemax={today.total}
							aria-valuenow={today.done}
							className="h-2 overflow-hidden rounded-full bg-bg-subtle"
						>
							<div
								className="h-full rounded-full bg-brand"
								style={{ width: `${(today.done / today.total) * 100}%` }}
							/>
						</div>
						<Figures
							rows={[
								["driver.dashboard.delivered", String(today.delivered)],
								["driver.dashboard.failed", String(today.failed)],
								["driver.dashboard.returned", String(today.returned)],
							]}
						/>
					</>
				)}
				<div className="flex flex-col gap-1">
					<h3 className="text-[13px] text-fg-muted">{t("driver.cash.inHand")}</h3>
					{(held.length === 0 ? [["USD", 0] as const] : held).map(([currency, amountMinor]) => (
						<p key={currency} className="text-[24px] font-bold text-fg">
							{money(amountMinor, currency)}
						</p>
					))}
				</div>
				<Button asChild variant="primary" size="lg">
					<Link to="/today">{t("driver.dashboard.openToday")}</Link>
				</Button>
			</section>

			{(
				[
					["driver.dashboard.week", span.week],
					["driver.dashboard.month", span.month],
				] as const
			).map(([title, since]) => (
				<section key={title} aria-label={t(title)} className="flex flex-col gap-2 card p-4">
					<h2 className="text-[15px] font-bold text-fg">{t(title)}</h2>
					{kpi.data && cod.data ? (
						<Period figures={figuresSince(since, kpi.data, cod.data)} money={money} />
					) : (
						<p className="text-[14px] text-fg-muted">
							{kpi.isError || cod.isError ? t("driver.dashboard.unavailable") : offline}
						</p>
					)}
				</section>
			))}

			<section aria-label={t("driver.cash.earnings")} className="flex flex-col gap-2 card p-4">
				<h2 className="text-[15px] font-bold text-fg">{t("driver.cash.earnings")}</h2>
				{earnings.data ? (
					<Figures
						rows={[
							["driver.cash.netOwed", money(earnings.data.netOwedMinor, earnings.data.currency)],
						]}
					/>
				) : (
					<p className="text-[14px] text-fg-muted">
						{t(
							!online
								? "driver.cash.earningsOffline"
								: earnings.isError
									? "driver.cash.earningsUnavailable"
									: "driver.today.loading",
						)}
					</p>
				)}
				<Button asChild size="lg">
					<Link to="/cash">{t("driver.dashboard.openCash")}</Link>
				</Button>
			</section>

			<Button asChild size="lg">
				<Link to="/history">{t("driver.dashboard.openHistory")}</Link>
			</Button>
		</div>
	);
}

/** One period's delivered, failed and COD collected. */
function Period({
	figures,
	money,
}: {
	figures: PeriodFigures;
	money: (amountMinor: number, currency: string) => string;
}) {
	const cod = Object.entries(figures.cod);
	return (
		<Figures
			rows={[
				["driver.dashboard.delivered", String(figures.delivered)],
				["driver.dashboard.failed", String(figures.failed)],
				[
					"driver.dashboard.codCollected",
					(cod.length === 0 ? [["USD", 0] as const] : cod)
						.map(([currency, amountMinor]) => money(amountMinor, currency))
						.join(" · "),
				],
			]}
		/>
	);
}

/** Labelled figures, a row each. */
function Figures({ rows }: { rows: [label: string, value: string][] }) {
	const { t } = useI18n();
	return (
		<dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 text-[14px]">
			{rows.map(([label, value]) => (
				<div key={label} className="contents">
					<dt className="text-fg-muted">{t(label)}</dt>
					<dd className="text-end font-bold text-fg">{value}</dd>
				</div>
			))}
		</dl>
	);
}
